import React, { createContext, useContext, useState, useEffect } from 'react';
import { DateTime } from 'luxon';

// Define common timezone options
export const TIMEZONE_OPTIONS = [
  { value: 'UTC', label: 'UTC' },
  { value: 'America/New_York', label: 'New York (EST/EDT)' },
  { value: 'America/Los_Angeles', label: 'Los Angeles (PST/PDT)' },
  { value: 'America/Chicago', label: 'Chicago (CST/CDT)' },
  { value: 'Europe/London', label: 'London (GMT/BST)' },
  { value: 'Europe/Paris', label: 'Paris (CET/CEST)' },
  { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
  { value: 'Asia/Shanghai', label: 'Shanghai (CST)' },
  { value: 'Asia/Ho_Chi_Minh', label: 'Ho Chi Minh (ICT)' },
  { value: 'Asia/Singapore', label: 'Singapore (SGT)' },
  { value: 'Australia/Sydney', label: 'Sydney (AEST/AEDT)' },
];

type TimezoneContextType = {
  timezone: string;
  setTimezone: (timezone: string) => void;
  formatDate: (date: string | Date | number | null) => string;
  formatDateTime: (date: string | Date | number | null) => string;
  formatTime: (date: string | Date | number | null) => string;
};

const TimezoneContext = createContext<TimezoneContextType | undefined>(undefined);

export const TimezoneProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Initialize timezone from localStorage or fall back to user's local timezone
  const [timezone, setTimezoneState] = useState<string>(() => {
    const savedTimezone = localStorage.getItem('app-timezone');
    if (savedTimezone) return savedTimezone;
    
    // Get user's local timezone using Luxon
    const localTimezone = DateTime.local().zoneName;
    return localTimezone || 'UTC';
  });

  // Update localStorage when timezone changes
  const setTimezone = (newTimezone: string) => {
    localStorage.setItem('app-timezone', newTimezone);
    setTimezoneState(newTimezone);
  };

  // Format date only (YYYY-MM-DD)
  const formatDate = (date: string | Date | number | null): string => {
    if (!date) return '—';
    return DateTime.fromJSDate(new Date(date))
      .setZone(timezone)
      .toFormat('yyyy-MM-dd');
  };

  // Format date and time (YYYY-MM-DD HH:MM:SS)
  const formatDateTime = (date: string | Date | number | null): string => {
    if (!date) return '—';
    return DateTime.fromJSDate(new Date(date))
      .setZone(timezone)
      .toFormat('yyyy-MM-dd HH:mm:ss');
  };

  // Format time only (HH:MM:SS)
  const formatTime = (date: string | Date | number | null): string => {
    if (!date) return '—';
    return DateTime.fromJSDate(new Date(date))
      .setZone(timezone)
      .toFormat('HH:mm:ss');
  };

  return (
    <TimezoneContext.Provider value={{ timezone, setTimezone, formatDate, formatDateTime, formatTime }}>
      {children}
    </TimezoneContext.Provider>
  );
};

// Custom hook to use the timezone context
export const useTimezone = (): TimezoneContextType => {
  const context = useContext(TimezoneContext);
  if (context === undefined) {
    throw new Error('useTimezone must be used within a TimezoneProvider');
  }
  return context;
};